import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerCallFlowExtraDataT } from '../PowerCallFlow';
import { ForwardModal as ActionModal } from '../modals/forward';
import { CallPattern } from '../types';

export type Payload = {
  callPattern: CallPattern;
  display_number: string;
  noAnswerNodeId: null | string;
  numbers: PhoneNumber[];
  recordCall: boolean;
};

export type PhoneNumber = {
  countryCode: null | string;
  id: string;
  isValid: boolean;
  phoneNumber: null | string;
  sip: null | string;
  timeout: null | string;
  type: string;
  user: null | string;
};

export type FlowCallNodeForward = NodeT<FlowNodeTypes.FORWARD, Payload>;

const portTranslator = (node: FlowCallNodeForward): RegisterPortT[] => {
  if (node.payload.noAnswerNodeId) {
    return [
      {
        id: uuid(),
        type: 'out',
        label: 'No answer',
        connection: node.payload.noAnswerNodeId,
      },
    ];
  }

  return [];
};

export class ForwardNode extends NodeTypeComponent<
  FlowCallNodeForward,
  PowerCallFlowExtraDataT
>() {
  static icon: IconNames = 'phone-outgoing';
  static label = 'Forward';
  static helpText =
    'Set up incoming calls to be forwarded to internal or external phone numbers.';
  static color = flowNodeColor.green;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.FORWARD };
  static updateConnection = (
    node: FlowCallNodeForward,
    port: InternalPortT,
    newConnection: string,
  ): FlowCallNodeForward => {
    const payload = { ...node.payload };
    if (port.label === 'No answer') {
      payload.noAnswerNodeId = newConnection;
    }

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  @boundMethod
  getCallPatternName(pattern: string): string {
    switch (pattern) {
      case CallPattern.Sequence:
        return 'Sequence';
      default:
        return 'First to pick up';
    }
  }

  render() {
    const {
      node: {
        payload: { callPattern, numbers },
      },
    } = this.props;

    return (
      <FlowNode
        color={ForwardNode.color}
        icon={ForwardNode.icon}
        label={ForwardNode.label}
        text={
          <span>
            Call pattern <strong>{this.getCallPatternName(callPattern)}</strong>
            , to{' '}
            <strong>
              {numbers.length} {numbers.length === 1 ? 'phone' : 'phones'}
            </strong>
          </span>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
